import { create } from 'zustand';

/**
 * Round-4 U7 (E3) — which play/playbook flow this browser session is
 * currently in, and where to send the rep back to.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 * `PlayFlowShell` used to clear every module-scope flow store on mount AND
 * in cleanup. The cleanup half made the browser Back button destroy a rep's
 * typed copy: React Router's popstate handler unmounts the shell before any
 * app listener can intervene, and a `useBlocker` dialog is not available
 * (react-router-dom 6.30.3 needs a data router; the app mounts
 * `<BrowserRouter>` at `web/app-home/src/main.tsx`). Rather than intercept
 * Back — which cannot be done here — round 4 makes Back harmless: nothing is
 * cleared on unmount, so the drafts are still in
 * `sequence-copy-draft-store`/`generated-copy-store` when the rep comes
 * back, exactly as those stores' unmount-survival contract already intends.
 *
 * The leak that the unmount clear was defending against (a DIFFERENT play
 * opening onto the previous play's typing — the keys carry no play identity)
 * is now closed on the other side: this store remembers which flow key was
 * last mounted, and {@link shouldClearFlowStores} makes the shell clear only
 * when that key actually changes.
 *
 * `returnHref` + `label` exist for `UnsavedEditsPill`: while the rep is away
 * from a flow that still holds unsaved work, the header shows a pill that
 * navigates straight back to it. The href is the full
 * `pathname + search` (not just the play id), so returning lands on the same
 * `?step=` the rep left from.
 */

export interface FlowSessionState {
  /** `flowContextKey(context)` of the flow currently (or most recently) mounted. `null` = none this session. */
  lastContextKey: string | null;
  /** `location.pathname + location.search` for {@link lastContextKey}, kept fresh across `?step=` changes. */
  returnHref: string | null;
  /** Rep-facing name for the pill — the flow's kind word today (see `PlayFlowShell`). */
  label: string | null;
}

/** Exported per the store convention — reset idiom is `useFlowSessionStore.setState(initialState, true)`. */
export const initialState: FlowSessionState = {
  lastContextKey: null,
  returnHref: null,
  label: null,
};

export const useFlowSessionStore = create<FlowSessionState>(() => ({ ...initialState }));

/**
 * The Back-button fix in one predicate: clear the flow stores ONLY when the
 * rep has moved from one flow to a genuinely different one.
 *
 * - `prev === null` — first mount of the session. Nothing to leak from, and
 *   clearing here is precisely what used to destroy a draft the rep left on
 *   this same play before a full page reload of a different route.
 * - `prev === next` — the same play re-entered (Back, then forward; the pill;
 *   a route remount) OR StrictMode's dev setup→cleanup→setup double-invoke.
 *   Both must be no-ops, which is what makes this effect idempotent without
 *   any ref bookkeeping.
 * - otherwise — a different play/playbook, or the same id through the other
 *   doorway (`flowContextKey` deliberately differs between the two kinds).
 */
export function shouldClearFlowStores(prev: string | null, next: string): boolean {
  return prev !== null && prev !== next;
}

/** Records the flow the shell just mounted. Called after any clear, never before. */
export function recordFlowSession(contextKey: string, returnHref: string, label: string): void {
  useFlowSessionStore.setState({ lastContextKey: contextKey, returnHref, label });
}

/**
 * Refreshes the return href as the rep moves between `?step=` tabs inside
 * the SAME flow. Guarded on `contextKey` so an unmounting shell's trailing
 * effect can never overwrite the href of the flow that just replaced it.
 */
export function updateFlowReturnHref(contextKey: string, returnHref: string): void {
  if (useFlowSessionStore.getState().lastContextKey !== contextKey) return;
  useFlowSessionStore.setState({ returnHref });
}

/**
 * Forgets the PILL's half of the session — the explicit-discard path only
 * (the guard dialog's "Discard changes" and the pill's own "Discard"),
 * alongside the draft-store clears. Never called from an unmount; that is the
 * whole point of this unit.
 *
 * `lastContextKey` is DELIBERATELY left alone (round-4 QA codex-review fix,
 * wave 1, finding 2). It used to `setState(initialState, true)`, which reset
 * the key to `null` too — and `shouldClearFlowStores` treats `prev === null`
 * as "first mount of the session, never clear". So a rep who discarded Play
 * A's work and then opened Play B hit the first-mount branch, the shell
 * skipped its whole clear list (rung scope, playbook draft, sequence
 * preview, members-panel collapse, …), and A's leftover state opened onto B.
 * The pill has no need for the key: it hides as soon as `hasUnsavedWork()`
 * is false, and `returnHref === null` already makes it unrenderable. Clearing
 * only the pill's two fields keeps discard honest about what it owns and
 * leaves the leak guard armed.
 */
export function clearFlowSession(): void {
  useFlowSessionStore.setState({ returnHref: initialState.returnHref, label: initialState.label });
}
